import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const formatMoney = (amount, currency = 'GHS') => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const paymentStatusColors = {
  paid: { bg: '#d1e7dd', text: '#0f5132' },
  pending: { bg: '#fff3cd', text: '#664d03' },
  failed: { bg: '#f8d7da', text: '#842029' },
};

const orderStatusColors = {
  processing: { bg: '#dbeafe', text: '#1e3a8a' },
  pending_payment: { bg: '#f3e8ff', text: '#6b21a8' },
  payment_failed: { bg: '#fee2e2', text: '#991b1b' },
};

const statusLabel = (value) => String(value || '').replace(/_/g, ' ');

function parseJsonSafely(text) {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

function StatusBadge({ value, palette }) {
  const style = palette[value] || { bg: '#e5e7eb', text: '#111827' };

  return (
    <span
      style={{
        background: style.bg,
        color: style.text,
        borderRadius: 999,
        padding: '4px 10px',
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'capitalize',
      }}
    >
      {statusLabel(value)}
    </span>
  );
}

export default function MyOrdersPage({ cartSummary }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const rawSession = localStorage.getItem('tk_fashion_user_session');
      const session = rawSession ? JSON.parse(rawSession) : null;
      const token = session?.token;

      if (!token) {
        throw new Error('Please log in to view your orders.');
      }

      const response = await fetch(`${API_BASE_URL}/api/orders/my`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payloadText = await response.text();
      const payload = parseJsonSafely(payloadText);

      if (!response.ok) {
        throw new Error(payload?.message || 'Unable to load your orders right now.');
      }

      const records = Array.isArray(payload?.data) ? payload.data : [];
      setOrders(records);
    } catch (fetchError) {
      setError(fetchError.message || 'Unable to load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const summary = useMemo(() => {
    return orders.reduce(
      (acc, order) => {
        acc.total += 1;
        if (order.paymentStatus === 'paid') acc.paid += 1;
        if (order.paymentStatus === 'pending') acc.pending += 1;
        if (order.paymentStatus === 'failed') acc.failed += 1;
        return acc;
      },
      { total: 0, paid: 0, pending: 0, failed: 0 }
    );
  }, [orders]);

  return (
    <section className="cart-page" style={{ maxWidth: 1060 }}>
      <header className="cart-page-header" style={{ marginBottom: 20 }}>
        <h2>My Orders</h2>
        <p>Track your orders with payment status and order status in one place.</p>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div className="cart-summary-card"><strong>{summary.total}</strong><span>Total Orders</span></div>
        <div className="cart-summary-card"><strong>{summary.paid}</strong><span>Paid</span></div>
        <div className="cart-summary-card"><strong>{summary.pending}</strong><span>Pending</span></div>
        <div className="cart-summary-card"><strong>{summary.failed}</strong><span>Failed</span></div>
      </div>

      <div className="cart-summary-card" style={{ marginBottom: 16 }}>
        <p style={{ margin: 0, color: '#475569' }}>
          Current Cart: <strong>{cartSummary?.quantity || 0}</strong> items · <strong>{formatMoney(cartSummary?.amount || 0)}</strong>
        </p>
      </div>

      {loading && <div className="cart-summary-card">Loading your orders...</div>}

      {!loading && error && (
        <div className="cart-summary-card" style={{ borderLeft: '4px solid #dc2626' }}>
          <p style={{ margin: 0 }}>{error}</p>
          <button type="button" className="checkout-button" onClick={fetchOrders} style={{ marginTop: 12 }}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && orders.length === 0 && (
        <div className="cart-summary-card">
          <p style={{ margin: 0 }}>No orders yet. Complete checkout to see your orders here.</p>
        </div>
      )}

      {!loading && !error && orders.length > 0 && (
        <div style={{ display: 'grid', gap: 12 }}>
          {orders.map((order) => (
            <article
              key={order._id}
              className="cart-item"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/orders/${encodeURIComponent(order.orderNumber)}`)}
            >
              <div className="cart-item-main">
                <h4 style={{ marginBottom: 6 }}>Order #{order.orderNumber}</h4>
                <p style={{ margin: 0, color: '#64748b' }}>
                  {order.items?.length || 0} item(s) · {formatMoney(order.totals?.total, order.totals?.currency)} · {formatDate(order.createdAt)}
                </p>
                <p style={{ margin: '8px 0 0', color: '#334155' }}>
                  {order.fulfillment?.method === 'pickup' ? 'Pickup' : 'Delivery'} · {order.fulfillment?.recipientName || 'Recipient not set'}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                <StatusBadge value={order.paymentStatus} palette={paymentStatusColors} />
                <StatusBadge value={order.orderStatus} palette={orderStatusColors} />
                <small style={{ color: '#64748b' }}>Tap to view details</small>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
